import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Money is stored as dollar floats, so naive math like `99.99 * 3` leaks binary
// rounding noise (299.96999999999997). Round to the nearest cent before formatting.
export function formatMoney(amount: number): string {
  return (Math.round(amount * 100) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function getRGBA(color: string) {
  if (typeof document === "undefined") {
    return color;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return color;
  }

  context.fillStyle = color;
  return context.fillStyle;
}

export function colorWithOpacity(color: string, opacity: number) {
  const normalizedOpacity = Math.min(Math.max(opacity, 0), 1);

  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const fullHex =
      hex.length === 3
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;

    const red = Number.parseInt(fullHex.slice(0, 2), 16);
    const green = Number.parseInt(fullHex.slice(2, 4), 16);
    const blue = Number.parseInt(fullHex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${normalizedOpacity})`;
  }

  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${normalizedOpacity})`);
  }

  if (color.startsWith("rgba(")) {
    return color.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/, `rgba($1,$2,$3, ${normalizedOpacity})`);
  }

  return color;
}
